// ================================
// MobileSidebarDrawer.tsx
// Drawer (SideBar no Mobile) com gesto de borda, busca, favoritos,
// módulos por obra e badges. Sem optional chaining e com self-tests opcionais.
// ================================
import {
  Banknote,
  BarChart3,
  BookOpen,
  Building,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  FileText,
  Image,
  List,
  MessageSquare,
  Package,
  Plus,
  Search,
  Settings,
  ShoppingCart,
  Star,
  StarOff,
  Users,
  Wrench,
} from 'lucide-react'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useSolicitadoCount } from '@/features/compras/services/comprasService'
import { useAuth } from '@/features/sistema/hooks/useAuth'
import { supabase } from '@/features/sistema/services/supabaseClient'
import QuickTaskCreator from '@/features/tarefas/components/QuickTaskCreator'

// Tipos mínimos
export type ObraStatus = 'em_andamento' | 'paralisada' | 'concluida' | string
export type ObraSlim = { id: string; nome: string; status: ObraStatus }
export type BadgeCounts = { tarefas?: number; diario?: number; servicos?: number; compras?: number; checklists?: number }

// Módulos (espelha a SideBar web)
const MODULES: {
  key: keyof BadgeCounts
     | 'cronograma'
     | 'tarefas'
     | 'diario'
     | 'servicos'
     | 'compras'
     | 'checklists'
     | 'midia'
     | 'financeiro'
     | 'ppc';
  label: string;
  icon: React.ElementType;
  path: (obraId: string)=>string;
}[] = [
  { key: 'cronograma', label: 'Cronograma', icon: Calendar, path: (id)=>`/obras/${id}/cronograma` },
  { key: 'tarefas',    label: 'Tarefas',    icon: List,      path: (id)=>`/obras/${id}/tarefas` },
  { key: 'diario',     label: 'Diário',     icon: BookOpen,  path: (id)=>`/obras/${id}/diarios` },
  { key: 'servicos',   label: 'Serviços',   icon: Wrench,    path: (id)=>`/obras/${id}/servicos` },
  { key: 'ppc',        label: 'PPC',        icon: BarChart3, path: (id)=>`/obras/${id}/ppc` },
  { key: 'compras',    label: 'Compras',    icon: ShoppingCart, path: (id)=>`/obras/${id}/compras` },
  { key: 'checklists', label: 'Checklists', icon: CheckSquare,  path: (id)=>`/obras/${id}/checklist` },
  { key: 'midia',      label: 'Mídia',      icon: Image,     path: (id)=>`/obras/${id}/fotos` },
  // Novo chip: Financeiro (por enquanto leva à rota GLOBAL com query da obra)
  { key: 'financeiro', label: 'Financeiro', icon: Banknote,  path: (id)=>`/financeiro?obra=${id}` },
]

// LocalStorage helper simples (sem optional chaining)
function useLocalStorage<T>(key: string, initial: T) {
  const [state, setState] = React.useState<T>(() => {
    try { const raw = localStorage.getItem(key); return raw ? (JSON.parse(raw) as T) : initial } catch { return initial }
  })
  React.useEffect(() => { try { localStorage.setItem(key, JSON.stringify(state)) } catch {} }, [key, state])
  return [state, setState] as const
}

interface MobileSidebarDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  getBadgeCounts?: (obraId: string) => Promise<BadgeCounts>
}

// Helpers sem optional chaining
function getUserMeta(user: any) {
  const am = user && user.app_metadata ? user.app_metadata : {}
  const um = user && user.user_metadata ? user.user_metadata : {}
  return { am, um }
}
function getUserId(user: any): string | undefined {
  return user && user.id ? user.id : undefined
}
function hasAdminMeta(user: any, isAdminProp: any): boolean {
  if (typeof isAdminProp !== 'undefined') return !!isAdminProp
  const meta = getUserMeta(user)
  const am = meta.am
  const um = meta.um
  const amRoles = am && am.roles && Array.isArray(am.roles) ? am.roles : []
  return !!(am && am.is_admin) || (amRoles.indexOf('admin') >= 0) || !!(um && (um.is_admin || um.perfil === 'admin' || um.role === 'admin'))
}
function safeClientXFromDOMTouchEvent(e: any): number {
  const t = e && e.touches && e.touches.length > 0 ? e.touches[0] : null
  return t ? t.clientX : 0
}
function safeClientXFromReactTouchEvent(e: any): number {
  const touches = e && (e as any).touches
  const t = touches && touches.length > 0 ? touches[0] : null
  return t ? t.clientX : 0
}
function computeExpanded(expandedObras: Record<string, boolean>, obraId: string, expandedDefault?: boolean): boolean {
  const hasValue = Object.prototype.hasOwnProperty.call(expandedObras, obraId)
  if (hasValue) return !!expandedObras[obraId]
  if (typeof expandedDefault !== 'undefined') return !!expandedDefault
  return false
}

export default function MobileSidebarDrawer({ open, onOpenChange, getBadgeCounts }: MobileSidebarDrawerProps) {
  const navigate = useNavigate()
  const location = useLocation()

  // Estados
  const [query, setQuery] = React.useState('')
  const [expandedConcluidas, setExpandedConcluidas] = useLocalStorage<boolean>('acor:sidebar:concluidas:expanded', false)
  const [expandedOutras, setExpandedOutras] = useLocalStorage<boolean>('acor:sidebar:outras:expanded', true)
  const [expandedObras, setExpandedObras] = useLocalStorage<Record<string, boolean>>('acor:sidebar:obras:expanded', {})
  const [favoriteIds, setFavoriteIds] = useLocalStorage<string[]>('acor:sidebar:favorites', [])
  const [expandedCadastros, setExpandedCadastros] = useLocalStorage<boolean>('acor:sidebar:cadastros:expanded', false)
  const [showQuickTask, setShowQuickTask] = React.useState(false)

  const [obras, setObras] = React.useState<ObraSlim[]>([])
  const [badges, setBadges] = React.useState<Record<string, BadgeCounts>>({})

  const { count: solicitadoCount } = useSolicitadoCount()
  const { user, isAdmin } = useAuth() as any

  // Variáveis para o botão Meu Perfil
  const displayName = user && user.user_metadata && user.user_metadata.nome ? user.user_metadata.nome : (user && user.email ? user.email : 'Usuário')
  const displayInitial = user && user.user_metadata && user.user_metadata.nome ? user.user_metadata.nome.charAt(0) : (user && user.email ? user.email.charAt(0) : 'U')

  const adminMeta = React.useMemo(() => hasAdminMeta(user, isAdmin), [user, isAdmin])

  const [adminDB, setAdminDB] = React.useState(false)
  React.useEffect(() => {
    let ignore = false
    ;(async () => {
      try {
        const uid = getUserId(user)
        if (!uid) { if (!ignore) setAdminDB(false); return }
        const { data, error } = await supabase.from('usuarios').select('role').eq('id', uid).single()
        const role = !error && data && (data as any).role ? (data as any).role : undefined
        if (!ignore) setAdminDB(role === 'admin')
      } catch { if (!ignore) setAdminDB(false) }
    })()
    return () => { ignore = true }
  }, [user])
  const admin = adminMeta || adminDB

  // Carrega obras (exclui pessoais)
  React.useEffect(() => {
    let isMounted = true
    ;(async () => {
      try {
        const { data, error } = await supabase
          .from('obras')
          .select('id, nome, status, is_personal')
          .or('is_personal.is.null,is_personal.eq.false')
          .order('nome', { ascending: true })
        if (error) throw error
        if (!isMounted) return
        setObras((data || []) as ObraSlim[])
      } catch(e) {
        console.warn('MobileSidebarDrawer: falha ao carregar obras', e)
        if (isMounted) setObras([])
      }
    })()
    return () => { isMounted = false }
  }, [])

  // Carrega badges por obra (opcional)
  React.useEffect(() => {
    if (!getBadgeCounts) return
    let cancelled = false
    ;(async () => {
      const next: Record<string, BadgeCounts> = {}
      for (let i = 0; i < obras.length; i++) {
        const o = obras[i]
        try {
          const c = await getBadgeCounts(o.id)
          if (cancelled) return
          next[o.id] = c
        } catch {}
      }
      if (!cancelled) setBadges(next)
    })()
    return () => { cancelled = true }
  }, [getBadgeCounts, obras])

  // Helpers
  const go = (href: string) => { onOpenChange(false); navigate(href) }
  const isFav = (id: string) => favoriteIds.indexOf(id) >= 0
  const toggleFav = (id: string) => {
    setFavoriteIds(prev => isFav(id) ? prev.filter(x=>x!==id) : prev.concat([id]))
    setExpandedObras(prev => { const next = { ...prev } as any; next[id] = true; return next })
  }
  const toggleExpandedObra = (id: string) => setExpandedObras(prev => ({ ...prev, [id]: !prev[id] }))

  const filtered = obras.filter(o => o.nome.toLowerCase().indexOf(query.toLowerCase()) >= 0)
  const favoritas = filtered.filter(o => isFav(o.id))
  const concluidas = filtered.filter(o => (o.status === 'concluida'))
  const outras = filtered.filter(o => !isFav(o.id) && o.status !== 'concluida')

  // ===== Gestos: edge-swipe para abrir e swipe para fechar =====
  const panelRef = React.useRef<HTMLDivElement | null>(null)
  const [dragging, setDragging] = React.useState(false)
  const [dragX, setDragX] = React.useState(0)
  const startX = React.useRef(0)
  const openingFromEdge = React.useRef(false)

  // Hotspot invisível na borda esquerda (sempre montado) para abrir
  React.useEffect(() => {
    const hotspot = document.getElementById('mobile-left-edge-hotspot')
    if (!hotspot) return
    const onStart = (e: any) => {
      openingFromEdge.current = true
      startX.current = safeClientXFromDOMTouchEvent(e)
      setDragging(true)
      setDragX(0)
    }
    const onMove = (e: any) => {
      if (!dragging || !openingFromEdge.current) return
      const cx = safeClientXFromDOMTouchEvent(e)
      const delta = Math.max(0, Math.min(cx - startX.current, 320))
      setDragX(delta)
    }
    const onEnd = () => {
      if (!openingFromEdge.current) { setDragging(false); setDragX(0); return }
      const shouldOpen = dragX > 80
      setDragging(false)
      setDragX(0)
      openingFromEdge.current = false
      if (shouldOpen) onOpenChange(true)
    }
    hotspot.addEventListener('touchstart', onStart, { passive: true })
    hotspot.addEventListener('touchmove', onMove, { passive: true })
    hotspot.addEventListener('touchend', onEnd)
    hotspot.addEventListener('touchcancel', onEnd)
    return () => {
      hotspot.removeEventListener('touchstart', onStart)
      hotspot.removeEventListener('touchmove', onMove)
      hotspot.removeEventListener('touchend', onEnd)
      hotspot.removeEventListener('touchcancel', onEnd)
    }
  }, [dragging, dragX, onOpenChange])

  // Swipe para fechar (arrastar painel para a esquerda)
  const onPanelTouchStart = (e: React.TouchEvent) => {
    startX.current = safeClientXFromReactTouchEvent(e)
    setDragging(true)
  }
  const onPanelTouchMove = (e: React.TouchEvent) => {
    if (!dragging) return
    const currentX = safeClientXFromReactTouchEvent(e)
    const delta = Math.min(0, currentX - startX.current) // negativo = arrastando para esquerda
    setDragX(delta)
  }
  const onPanelTouchEnd = () => {
    const shouldClose = dragX < -80
    setDragging(false)
    setDragX(0)
    if (shouldClose) onOpenChange(false)
  }

  const panelStyle: React.CSSProperties = open
    ? { transform: dragging && dragX < 0 ? `translateX(${dragX}px)` : 'translateX(0)' }
    : { transform: dragging && openingFromEdge.current ? `translateX(calc(-100% + ${dragX}px))` : 'translateX(-100%)' }

  // Render
  return (
    <>
      {/* Hotspot de gesto no lado esquerdo - invisível, sempre ativo no mobile */}
      <div id="mobile-left-edge-hotspot" className="fixed left-0 top-0 h-[100dvh] w-3 z-30 md:hidden" style={{ touchAction: 'pan-y' }} />

      {/* Backdrop */}
      <div
        aria-hidden={!open}
        className={`fixed inset-0 z-50 md:hidden transition-opacity duration-200 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => onOpenChange(false)}
      >
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* Painel */}
      <aside
        ref={panelRef}
        onTouchStart={open ? onPanelTouchStart : undefined}
        onTouchMove={open ? onPanelTouchMove : undefined}
        onTouchEnd={open ? onPanelTouchEnd : undefined}
        className="fixed left-0 top-0 z-[60] h-[100dvh] w-[86vw] max-w-[320px] md:hidden
                   bg-white dark:bg-gray-900 border-r border-neutral-200 dark:border-neutral-800
                   shadow-xl transition-transform duration-200"
        style={panelStyle}
      >
        {/* Header (busca) */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-200 dark:border-neutral-800" style={{ paddingTop: 'max(env(safe-area-inset-top), 8px)' }}>
          <div className="flex items-center gap-1.5 flex-1 bg-neutral-50 dark:bg-neutral-900 rounded px-2 py-1">
            <Search className="h-4 w-4 text-neutral-700 dark:text-neutral-300" />
            <input
              value={query}
              onChange={function (e) { setQuery(e.target.value) }}
              placeholder="Buscar obra..."
              className="bg-transparent outline-none text-[13px] leading-5 w-full placeholder:text-neutral-400 dark:placeholder:text-neutral-500 text-neutral-800 dark:text-neutral-200"
            />
          </div>
        </div>

        {/* Ações Globais */}
        <div className="px-3 pt-2 space-y-1">
          {/* Nova Tarefa (destaque) */}
          <button
            onClick={function () { setShowQuickTask(true) }}
            className="w-full flex items-center gap-2 rounded-md border px-3 py-2 border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200"
          >
            <Plus className="h-4 w-4" />
            <span className="text-[13px] leading-5 font-medium">Nova Tarefa</span>
          </button>
          {/* Assistente */}
          <button
            onClick={function () {
              onOpenChange(false);
              try {
                window.dispatchEvent(new CustomEvent('acor-open-chat', { detail: { source: 'mobile-drawer' } }))
              } catch (e) {
                console.warn('Assistente (mobile) falhou ao abrir:', e)
              }
            }}
            className="w-full flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-800 dark:text-neutral-200"
            aria-label="Abrir Assistente"
          >
            <MessageSquare className="h-4 w-4" />
            <span className="text-[13px] leading-5">Assistente</span>
          </button>
          {/* Compras */}
          <button onClick={function () { go('/compras') }} className="relative w-full flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-800 dark:text-neutral-200">
            <ShoppingCart className="h-4 w-4" />
            <span className="text-[13px] leading-5">Compras</span>
            {solicitadoCount > 0 && (
              <span className="absolute top-1 right-2 min-w-[16px] h-[16px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                {solicitadoCount > 99 ? '99+' : solicitadoCount}
              </span>
            )}
          </button>
          {/* Financeiro */}
          <button onClick={function () { go('/financeiro') }} className="w-full flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-800 dark:text-neutral-200">
            <Banknote className="h-4 w-4" />
            <span className="text-[13px] leading-5">Financeiro</span>
          </button>
          {/* RH */}
          <button
            onClick={async function () {
              try {
                const { data: { session } } = await supabase.auth.getSession()
                if (session && session.access_token && session.refresh_token) {
                  const url = new URL('https://acor.eng.br/rh/')
                  url.searchParams.set('access_token', session.access_token)
                  url.searchParams.set('refresh_token', session.refresh_token)
                  window.location.href = url.toString()
                } else {
                  console.error('Sessão não encontrada')
                }
              } catch (error) {
                console.error('Erro ao redirecionar para RH:', error)
              }
            }}
            className="w-full flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-800 dark:text-neutral-200"
          >
            <Users className="h-4 w-4" />
            <span className="text-[13px] leading-5">RH</span>
          </button>
        </div>

        {/* Listas de Obras */}
        <div className="px-3 py-2 overflow-y-auto min-h-0 h-[calc(100dvh-200px)] pb-32 webkit-scrollbar-hide">
          {/* Favoritas */}
          <div className="text-[11px] uppercase tracking-wide text-neutral-600 dark:text-neutral-400 mb-1.5 mt-1.5">Favoritas</div>
          {favoritas.map(function (o) { return (
            <ObraBlock key={o.id} obra={o} expandedDefault variant="favorite" expandedObras={expandedObras} toggleExpandedObra={toggleExpandedObra} isFav={isFav} toggleFav={toggleFav} badges={badges} locationPathname={location.pathname} go={go} />
          )})}

          {/* Outras */}
          <button onClick={function () { setExpandedOutras(!expandedOutras) }} className="mt-3 mb-1.5 flex items-center gap-1.5 w-full text-left text-[11px] uppercase tracking-wide text-neutral-600 dark:text-neutral-400 hover:underline">
            {expandedOutras ? <ChevronDown className="h-3.5 w-3.5 text-neutral-600 dark:text-neutral-400" /> : <ChevronRight className="h-3.5 w-3.5 text-neutral-600 dark:text-neutral-400" />}
            <span>Outras obras</span>
          </button>
          {expandedOutras && outras.map(function (o) { return (
            <ObraBlock key={o.id} obra={o} variant="other" expandedObras={expandedObras} toggleExpandedObra={toggleExpandedObra} isFav={isFav} toggleFav={toggleFav} badges={badges} locationPathname={location.pathname} go={go} />
          )})}

          {/* Concluídas */}
          <div className="mt-3">
            <button onClick={function () { setExpandedConcluidas(!expandedConcluidas) }} className="flex items-center gap-1.5 w-full text-left text-[13px] leading-5 text-neutral-700 dark:text-neutral-300 hover:underline">
              {expandedConcluidas ? <ChevronDown className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" /> : <ChevronRight className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />}
              <span>Obras concluídas</span>
            </button>
            {expandedConcluidas && concluidas.map(function (o) { return (
              <ObraBlock key={o.id} obra={o} variant="done" expandedObras={expandedObras} toggleExpandedObra={toggleExpandedObra} isFav={isFav} toggleFav={toggleFav} badges={badges} locationPathname={location.pathname} go={go} />
            )})}
          </div>

          {/* Rodapé com atalhos adicionais */}
          <div className="mt-4 border-t border-neutral-200 dark:border-neutral-800 pt-2">
            <div className="space-y-1">
              {/* Dashboards */}
              <button onClick={function () { go('/dashboards') }} className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200">
                <BarChart3 className="h-3.5 w-3.5" />
                <span>Dashboards</span>
              </button>
              {/* Serviços (geral) */}
              <button onClick={function () { go('/servicos/geral') }} className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200">
                <Wrench className="h-3.5 w-3.5" />
                <span>Serviços (geral)</span>
              </button>
              {/* Orçamentos */}
              <button onClick={function () { go('/orcamentos') }} className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200">
                <FileText className="h-3.5 w-3.5" />
                <span>Orçamentos</span>
              </button>

              {/* Cadastros (seção colapsável) */}
              <div className="mt-2">
                <button
                  onClick={function () { setExpandedCadastros(!expandedCadastros) }}
                  className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200"
                  title={expandedCadastros ? 'Recolher Cadastros' : 'Expandir Cadastros'}
                >
                  {expandedCadastros ? <ChevronDown className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" /> : <ChevronRight className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />}
                  <Settings className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />
                  <span>Cadastros</span>
                </button>
                {expandedCadastros && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-neutral-200 dark:border-neutral-700 pl-2">
                    {/* Admin-only: Nova Obra */}
                    {admin && (
                      <button onClick={function () { go('/obras/nova') }} className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200">
                        <Building className="h-3.5 w-3.5" />
                        <span>Nova Obra</span>
                      </button>
                    )}
                    {/* Admin-only: Gerenciar Usuários */}
                    {admin && (
                      <button onClick={function () { go('/usuarios') }} className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200">
                        <Users className="h-3.5 w-3.5" />
                        <span>Gerenciar Usuários</span>
                      </button>
                    )}
                    <button onClick={function () { go('/empresas') }} className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200">
                      <Building className="h-3.5 w-3.5" />
                      <span>Empresas</span>
                    </button>
                    <button onClick={function () { go('/configuracoes/materiais-catalogo') }} className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200">
                      <Package className="h-3.5 w-3.5" />
                      <span>Materiais Catálogo</span>
                    </button>
                    <button onClick={function () { go('/checklists/templates') }} className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200">
                      <CheckSquare className="h-3.5 w-3.5" />
                      <span>Itens Checklist</span>
                    </button>
                    <button onClick={function () { go('/cadastros/ppc-categorias') }} className="w-full flex items-center gap-2 rounded-md px-2 py-1 text-[13px] leading-5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60 text-neutral-800 dark:text-neutral-200">
                      <BarChart3 className="h-3.5 w-3.5" />
                      <span>Categorias PPC</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Meu Perfil — agora por último */}
              <button
                onClick={function () { go('/configuracoes') }}
                className="w-full mt-1.5 flex items-center gap-2.5 rounded-md border px-2.5 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-900/60"
                title="Meu Perfil"
              >
                <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-[12px]">
                  {displayInitial}
                </div>
                <div className="flex-1 text-left">
                  <div className="text-[13px] leading-5 font-medium text-neutral-900 dark:text-neutral-100">Meu Perfil</div>
                  <div className="text-[11px] text-neutral-600 dark:text-neutral-300 truncate">{displayName}</div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Task */}
        <QuickTaskCreator isOpen={showQuickTask} onClose={function () { setShowQuickTask(false) }} onSuccess={function () { setShowQuickTask(false) }} />
      </aside>
    </>
  )
}

// Item expandível por obra
function ObraBlock({ obra, expandedDefault, expandedObras, toggleExpandedObra, isFav, toggleFav, badges, locationPathname, go }:{
  obra: ObraSlim
  expandedDefault?: boolean
  variant: 'favorite' | 'other' | 'done'
  expandedObras: Record<string, boolean>
  toggleExpandedObra: (id: string) => void
  isFav: (id: string) => boolean
  toggleFav: (id: string) => void
  badges: Record<string, BadgeCounts>
  locationPathname: string
  go: (href: string) => void
}) {
  const expanded = computeExpanded(expandedObras, obra.id, expandedDefault)
  const toggle = function () { toggleExpandedObra(obra.id) }
  return (
    <div className="mb-1.5">
      <div className="relative flex items-center justify-between rounded-md bg-neutral-50 dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-800 px-2 py-1">
        <button onClick={toggle} className="flex-1 min-w-0 flex items-center gap-1.5 text-left">
          {expanded ? <ChevronDown className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" /> : <ChevronRight className="h-3.5 w-3.5 text-neutral-700 dark:text-neutral-300" />}
          <span className="truncate text-[13px] leading-5 text-neutral-900 dark:text-neutral-100">{obra.nome}</span>
        </button>
        <button onClick={function () { toggleFav(obra.id) }} className="shrink-0 ml-2 p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-700" title={isFav(obra.id) ? 'Remover dos favoritos' : 'Favoritar obra'}>
          {isFav(obra.id) ? <Star className="h-3.5 w-3.5 text-amber-500" /> : <StarOff className="h-3.5 w-3.5 text-neutral-500 dark:text-neutral-400 opacity-60" />}
        </button>
      </div>
      {expanded && (
        <nav className="mt-0.5 pl-6 space-y-0.5">
          {MODULES.map(function (m) {
            const Icon = m.icon as any
            const href = m.path(obra.id)
            const b = badges[obra.id] || {}
            const badgeValue = m.key === 'tarefas' ? b.tarefas : m.key === 'diario' ? b.diario : m.key === 'servicos' ? b.servicos : m.key === 'compras' ? b.compras : m.key === 'checklists' ? b.checklists : undefined
            const active = locationPathname === href
            return (
              <button key={String(m.key) + obra.id} onClick={function () { go(href) }} className={`w-full flex items-center justify-between rounded-md px-2 py-1 text-[13px] leading-5 ${active ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200' : 'hover:bg-neutral-50 dark:hover:bg-neutral-900/50 text-neutral-800 dark:text-neutral-200'}`}>
                <span className="flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5 text-neutral-600 dark:text-neutral-400" />
                  <span className="truncate">{m.label}</span>
                </span>
                {badgeValue ? (
                  <span className="ml-2 rounded-md bg-neutral-200 dark:bg-neutral-800 px-1.5 text-[10px] leading-4 text-neutral-800 dark:text-neutral-200">{badgeValue}</span>
                ) : null}
              </button>
            )
          })}
        </nav>
      )}
    </div>
  )
}

// ================================
// Self-tests leves (opcional)
// Rode definindo window.__RUN_ACOR_MOBILE_DRAWER_TESTS__ = true
// ================================
(function runSelfTests(){
  try {
    if (typeof window === 'undefined') return
    if (!(window as any).__RUN_ACOR_MOBILE_DRAWER_TESTS__) return

    // DOM touch
    var domEvtMock = { touches: [ { clientX: 123 } ] }
    console.assert(safeClientXFromDOMTouchEvent(domEvtMock) === 123, 'DOM touch clientX deve ser 123')
    var domEvtMock2 = { touches: [] }
    console.assert(safeClientXFromDOMTouchEvent(domEvtMock2) === 0, 'DOM touch vazio deve ser 0')
    var domEvtMock3 = { }
    console.assert(safeClientXFromDOMTouchEvent(domEvtMock3) === 0, 'DOM touch ausente deve ser 0')

    // React touch
    var rEvt = { touches: [ { clientX: 77 } ] }
    console.assert(safeClientXFromReactTouchEvent(rEvt) === 77, 'React touch clientX deve ser 77')
    var rEvt2 = { touches: [] }
    console.assert(safeClientXFromReactTouchEvent(rEvt2) === 0, 'React touch vazio deve ser 0')

    // computeExpanded
    var expA = computeExpanded({ a: true }, 'a', false)
    var expB = computeExpanded({}, 'b', true)
    var expC = computeExpanded({}, 'c')
    console.assert(expA === true, 'Expanded A true')
    console.assert(expB === true, 'Expanded B true por default')
    console.assert(expC === false, 'Expanded C false sem valor nem default')

    // hasAdminMeta
    var userAdmin = { app_metadata: { is_admin: true }, user_metadata: {} }
    console.assert(hasAdminMeta(userAdmin, undefined) === true, 'Admin via app_metadata')
    var userRole = { app_metadata: { roles: ['viewer','admin'] }, user_metadata: {} }
    console.assert(hasAdminMeta(userRole, undefined) === true, 'Admin via roles')
    var userUm = { app_metadata: {}, user_metadata: { role: 'admin' } }
    console.assert(hasAdminMeta(userUm, undefined) === true, 'Admin via user_metadata.role')
    var userNone = { app_metadata: {}, user_metadata: {} }
    console.assert(hasAdminMeta(userNone, undefined) === false, 'Não admin quando não há flags')

    console.log('[MobileSidebarDrawer] Self-tests OK')
  } catch (e) {
    console.warn('[MobileSidebarDrawer] Self-tests falharam:', e)
    // Se algum teste falhar, apenas logar para inspeção manual
  }
})()
